import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  BookOpen, ClipboardCheck, TrendingUp,
  ChevronRight, PenTool
} from 'lucide-react';
import axios from 'axios';
import ProficiencySpiderChart from '@/components/ProficiencySpiderChart';
import NYReadinessCard from '@/components/NYReadinessCard';
import MultipleMeasuresDisclaimer from '@/components/MultipleMeasuresDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OverviewTab = ({ classes, pendingGrading, setActiveTab }) => {
  const [spiderData, setSpiderData] = useState([]);

  useEffect(() => {
    axios.get(`${API}/analytics/spider/by-class`).then(res => {
      setSpiderData(res.data.classes || []);
    }).catch(() => {});
  }, []);
  return (
    <>
      {/* MOSAIC-NY1 · Pillar 1 · NY 2027 Readiness Card */}
      <div className="mb-6" data-testid="overview-ny-readiness-slot">
        <NYReadinessCard />
      </div>

      {/* MOSAIC-NY1 · Pillar 4 · Multiple-Measures Disclaimer */}
      <MultipleMeasuresDisclaimer variant="compact" className="mb-6" />

      {/* Pending Grading Banner */}
      {pendingGrading.length > 0 && (
        <div
          className="mb-6 border-2 border-orange-400 bg-orange-50 p-5 flex items-center justify-between cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => setActiveTab('grading')}
          data-testid="grading-banner"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-orange-500 text-white flex items-center justify-center font-bold text-2xl flex-shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {pendingGrading.length}
            </div>
            <div>
              <h3 className="font-bold text-lg text-orange-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Student Responses Need Scoring
              </h3>
              <p className="text-sm text-orange-700">
                {pendingGrading.filter(p => p.question?.modality === 'writing').length} Writing and {pendingGrading.filter(p => p.question?.modality === 'speaking').length} Speaking responses are waiting for your proficiency rubric scores.
              </p>
            </div>
          </div>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-6 flex-shrink-0" data-testid="grading-banner-btn">
            <PenTool size={18} className="mr-2" />
            Score Now
            <ChevronRight size={18} className="ml-1" />
          </Button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        {/* Classes */}
        <div className="border-2 border-gray-200">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-bold">My Classes</h3>
            <Link to="/grades">
              <Button variant="outline" size="sm" className="btn-sharp">
                Start Exam
              </Button>
            </Link>
          </div>
          {classes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No classes assigned yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {classes.map(cls => (
                <div 
                  key={cls.class_id} 
                  onClick={() => setActiveTab('classes')}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer"
                  data-testid={`overview-class-${cls.class_id}`}
                >
                  <div>
                    <div className="font-semibold">{cls.name}</div>
                    <div className="text-sm text-gray-500">
                      Grade {cls.grade_level} • {cls.student_ids?.length || 0} students
                    </div>
                  </div>
                  <ChevronRight className="text-gray-400" size={20} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="border-2 border-gray-200">

          {/* Spider Chart */}
          {spiderData.length > 0 && (
            <div className="p-4 border-b border-gray-200">
              <ProficiencySpiderChart
                series={spiderData.map(c => ({ name: c.class_name, ...c }))}
                title="Class Performance by Modality"
                subtitle="Average proficiency scores across Listening, Speaking, Reading, Writing"
                height={280}
              />
            </div>
          )}

          <div className="p-4 border-b border-gray-200">
            <h3 className="font-bold">Quick Actions</h3>
          </div>
          <div className="p-4 space-y-3">
            {pendingGrading.length > 0 && (
              <Button
                className="w-full justify-start h-12 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => setActiveTab('grading')}
                data-testid="quick-action-grading"
              >
                <PenTool className="mr-3" size={18} />
                Score Student Responses
                <span className="ml-auto bg-white text-orange-600 font-bold text-xs px-2 py-0.5 rounded-full">{pendingGrading.length}</span>
              </Button>
            )}
            <Link to="/grades" className="block">
              <Button className="w-full btn-sharp-primary justify-start h-12">
                <BookOpen className="mr-3" size={18} />
                Start New Assessment
              </Button>
            </Link>
            <Link to="/teacher-key" className="block">
              <Button variant="outline" className="w-full btn-sharp justify-start h-12">
                <ClipboardCheck className="mr-3" size={18} />
                View Answer Keys
              </Button>
            </Link>
            <Link to="/results" className="block">
              <Button variant="outline" className="w-full btn-sharp justify-start h-12">
                <TrendingUp className="mr-3" size={18} />
                View All Results
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default OverviewTab;
